/**
 * Theme contract shared by the Blueprint API and web app.
 * CSS variable names MUST stay in sync with @mn/ui (packages/ui/src/styles/globals.css).
 * Light mode only.
 */

export type ColorScaleStep = '50' | '100' | '200' | '300' | '400' | '500' | '600' | '700' | '800' | '900';
export type ColorScale = Record<ColorScaleStep, string>;

export interface ThemeColors {
  primary: ColorScale;
  accent: ColorScale;
  background: string;
  cardBg: string;
  cardBorder: string;
  success: string;
  warning: string;
  error: string;
  info: string;
}

export interface ThemeFonts {
  body: string;
  heading: string;
  mono: string;
}

export interface ThemeSettings {
  name: string;
  colors: ThemeColors;
  fonts: ThemeFonts;
  /** CSS length, e.g. "0.375rem" */
  borderRadius: string;
}

/** Deep-partial for PATCH-style updates from the admin editor. */
export type ThemeSettingsPatch = {
  name?: string;
  colors?: Partial<Omit<ThemeColors, 'primary' | 'accent'>> & {
    primary?: Partial<ColorScale>;
    accent?: Partial<ColorScale>;
  };
  fonts?: Partial<ThemeFonts>;
  borderRadius?: string;
};

// Defaults mirror @mn/ui :root (indigo primary / violet accent), with a soft slate
// page background so white cards pop (richer than pure-white #ffffff).
const INDIGO: ColorScale = {
  '50': '#eef2ff', '100': '#e0e7ff', '200': '#c7d2fe', '300': '#a5b4fc', '400': '#818cf8',
  '500': '#6366f1', '600': '#4f46e5', '700': '#4338ca', '800': '#3730a3', '900': '#312e81',
};
const VIOLET: ColorScale = {
  '50': '#f5f3ff', '100': '#ede9fe', '200': '#ddd6fe', '300': '#c4b5fd', '400': '#a78bfa',
  '500': '#8b5cf6', '600': '#7c3aed', '700': '#6d28d9', '800': '#5b21b6', '900': '#4c1d95',
};

export const DEFAULT_THEME_SETTINGS: ThemeSettings = {
  name: 'Default',
  colors: {
    primary: INDIGO,
    accent: VIOLET,
    background: '#f8fafc',
    cardBg: '#ffffff',
    cardBorder: '#e5e7eb',
    success: '#22c55e',
    warning: '#f59e0b',
    error: '#ef4444',
    info: '#3b82f6',
  },
  fonts: {
    body: "'Inter', system-ui, sans-serif",
    heading: "'Inter', system-ui, sans-serif",
    mono: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  },
  borderRadius: '0.375rem',
};

/** Map a full ThemeSettings to the @mn/ui CSS variable contract. */
export function themeToCssVariables(theme: ThemeSettings): Record<string, string> {
  const vars: Record<string, string> = {};
  for (const [step, hex] of Object.entries(theme.colors.primary)) vars[`--color-primary-${step}`] = hex;
  for (const [step, hex] of Object.entries(theme.colors.accent)) vars[`--color-accent-${step}`] = hex;
  vars['--background'] = theme.colors.background;
  vars['--card-bg'] = theme.colors.cardBg;
  vars['--card-border'] = theme.colors.cardBorder;
  vars['--success'] = theme.colors.success;
  vars['--warning'] = theme.colors.warning;
  vars['--error'] = theme.colors.error;
  vars['--info'] = theme.colors.info;
  vars['--font-body'] = theme.fonts.body;
  vars['--font-heading'] = theme.fonts.heading;
  vars['--font-mono'] = theme.fonts.mono;
  vars['--radius'] = theme.borderRadius;
  return vars;
}

/** Deep-merge a patch onto a base (used by the API service). */
export function mergeThemeSettings(base: ThemeSettings, patch: ThemeSettingsPatch): ThemeSettings {
  return {
    name: patch.name ?? base.name,
    colors: {
      ...base.colors,
      ...patch.colors,
      primary: { ...base.colors.primary, ...patch.colors?.primary },
      accent: { ...base.colors.accent, ...patch.colors?.accent },
    },
    fonts: { ...base.fonts, ...patch.fonts },
    borderRadius: patch.borderRadius ?? base.borderRadius,
  };
}

const SCALE_STEPS: ColorScaleStep[] = ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900'];

// How far each step is mixed toward white (negative) or black (positive) relative to
// the 500 base. 500 is the base color itself.
const SCALE_MIX: Record<ColorScaleStep, number> = {
  '50': -0.85, '100': -0.7, '200': -0.5, '300': -0.3, '400': -0.15,
  '500': 0, '600': 0.12, '700': 0.25, '800': 0.4, '900': 0.55,
};

function clampChannel(n: number): number {
  return Math.max(0, Math.min(255, Math.round(n)));
}

function hexToRgb(hex: string): [number, number, number] {
  const normalized = hex.replace('#', '');
  const full = normalized.length === 3
    ? normalized.split('').map((c) => c + c).join('')
    : normalized;
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ];
}

function rgbToHex(r: number, g: number, b: number): string {
  return `#${[r, g, b].map((c) => clampChannel(c).toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Derive a full 50–900 scale from a single base color.
 * Negative mix lightens toward white, positive darkens toward black; 500 is the base.
 * Pure (no deps) so it can run on the API or in the browser.
 */
export function generateColorScale(base: string): ColorScale {
  const [r, g, b] = hexToRgb(base);
  const scale = {} as ColorScale;
  for (const step of SCALE_STEPS) {
    const mix = SCALE_MIX[step];
    const target = mix < 0 ? 255 : 0;
    const amount = Math.abs(mix);
    scale[step] = rgbToHex(
      r + (target - r) * amount,
      g + (target - g) * amount,
      b + (target - b) * amount,
    );
  }
  return scale;
}
